import {
  WSActions,
  WSCard,
  WSElement,
  WSInputNumber,
  WSList,
  WSSectionToolbar,
  WSText,
  useIsMobile,
  useWSSnackbar
} from "@wingspanhq/fe-component-library";
import { AccountStatus } from "@wingspanhq/users/dist/lib/interfaces";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { Nullable } from "primereact/ts-helpers";
import { useMemo, useState } from "react";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { useMutationCreateInternalAccountTransfer } from "../../../../query/banking/mutations/useMutationCreateInternalAccountTransfer";
import { useQueryInternalAccounts } from "../../../../query/banking/queries/useQueryInternalAccounts";
import { useUserId } from "../../../../query/hooks/helpers";
import { usePayoutSettings } from "../../../../query/payments/queries";
import { usersService } from "../../../../services/users";
import { FromTo } from "../../../../shared/components/FromTo";
import { selectorActiveBankingAccount } from "../../../../shared/selectors/selectorActiveBankingAccount";
import { selectorActiveTaxWithholdingAccount } from "../../../../shared/selectors/selectorActiveTaxWithholdingAccount";
import { selectorIsPlatformBalanceAccount } from "../../../PlatformBalance/selectors/selectorIsPlatformBalanceAccount";
import { LabelMethod } from "../../../Settings/components/LabelMethod";
import { MethodValue } from "../../../Settings/components/ManageMethods/types";
import { TransferBalance, TransferType } from "../../types";
import { useModalAuthorizeACH } from "../ModalAuthorizeACH";
import { DropdownAccount } from "./DropdownAccount";
import { useModalReconnectAccount } from "./ModalReconnectAccount";

type Props = {
  balance: TransferBalance;
  type: TransferType;
  onCancel: () => void;
  onSuccess: () => void;
};

export const FormTransfer: React.FC<Props> = ({
  type,
  balance,
  onCancel,
  onSuccess
}) => {
  const isMobile = useIsMobile();
  const { openSnackbar } = useWSSnackbar();

  const userId = useUserId();
  const queryInternalAccounts = useQueryInternalAccounts();
  const queryPayoutSettings = usePayoutSettings(userId);

  const modalReconnectAccount = useModalReconnectAccount();
  const modalAuthorizeACH = useModalAuthorizeACH();

  const internalAccount = useMemo(() => {
    switch (balance) {
      case "wallet":
        return queryInternalAccounts.data?.find(selectorActiveBankingAccount);
      case "taxWithholdings":
        return queryInternalAccounts.data?.find(
          selectorActiveTaxWithholdingAccount
        );
      case "platform":
        return queryInternalAccounts.data?.find(internalAccount =>
          selectorIsPlatformBalanceAccount(
            internalAccount,
            queryPayoutSettings.data
          )
        );
      default:
        return;
    }
  }, [queryInternalAccounts.data, queryPayoutSettings.data, balance]);

  const [amount, setAmount] = useState<number | null>(null);
  const [selectedMethod, setSelectedMethod] =
    useState<Nullable<MethodValue>>(null);

  const [transfer, transferMeta] = useMutationCreateInternalAccountTransfer({
    onSuccess: () => {
      openSnackbar({
        message: "Transfer successfully initiated"
      });
      onSuccess();
    }
  });

  const handleCompleteTransfer = async () => {
    if (!selectedMethod) {
      console.assert(false, "selectedMethod is not set");
      return;
    }

    if (!amount) {
      console.assert(false, "amount is not set");
      return;
    }

    if (!internalAccount) {
      console.assert(false, "internalAccount is not set");
      return;
    }

    // Show ach modal for external accounts

    if (type === "deposit" && selectedMethod.type === "Account") {
      const authorized = await modalAuthorizeACH.open({
        accountId: selectedMethod.id,
        balance,
        amount
      });

      if (!authorized) {
        return;
      }
    }

    // Show reconnect modal for external accounts
    if (selectedMethod.type === "Account") {
      try {
        const account = await usersService.account.get(selectedMethod.id);

        const shouldRecconnect =
          type === "deposit" &&
          account.status === AccountStatus.Disconnected &&
          !!account.publicToken;

        if (shouldRecconnect) {
          const isReconnected = await modalReconnectAccount.open({
            token: account.publicToken || ""
          });

          if (!isReconnected) {
            return;
          }
        }
      } catch (error) {
        console.error(error);
        return;
      }
    }

    transfer({
      internalAccountId: internalAccount.internalAccountId,
      amount,
      direction: type === "deposit" ? "Deposit" : "Withdrawal",
      description: "Transfer from platform balance",
      transferMethod: "ACH",
      counterpartyAccountId: selectedMethod.id,
      counterpartyAccountType:
        selectedMethod.type === "InternalAccount"
          ? "InternalAccount"
          : "Account"
    });
  };

  const [showConfirmation, setShowConfirmation] = useState(false);

  const balancePreview = internalAccount && (
    <LabelMethod
      type="InternalAccount"
      id={internalAccount.internalAccountId}
      showBalance={type === "withdrawal"}
    />
  );

  const account =
    showConfirmation && selectedMethod ? (
      <LabelMethod type={selectedMethod.type} id={selectedMethod.id} />
    ) : (
      <DropdownAccount
        balance={balance}
        type={type}
        value={selectedMethod}
        onChange={setSelectedMethod}
      />
    );

  return (
    <WSList gap="2XL">
      <WSElement>
        <WSSectionToolbar
          title={
            type === "deposit"
              ? `Transfer to ${
                  balance === "wallet" ? "Wallet" : "Tax withholdings"
                }`
              : "Transfer to bank account"
          }
        />
        <WSText.ParagraphSm color="gray500">
          How much would you like to transfer?
        </WSText.ParagraphSm>
      </WSElement>

      <WSCard header="Amount">
        <WSList gap="XL">
          {showConfirmation ? (
            <WSText.Heading1 color="gray500" weight="book">
              {formatMoney(amount!, internalAccount?.currency || "USD")}
            </WSText.Heading1>
          ) : (
            <WSInputNumber
              size="L"
              value={amount}
              onChange={setAmount}
              currency={internalAccount?.currency || "USD"}
              maxFractionDigits={2}
            />
          )}

          <FromTo
            fromLabel="From"
            fromContent={type === "deposit" ? account : balancePreview}
            toLabel="To"
            toContent={type === "deposit" ? balancePreview : account}
          />
        </WSList>
      </WSCard>

      <WSErrorMessage
        contextKey="PendingWithholding"
        error={transferMeta.error}
      />

      {showConfirmation ? (
        <WSActions
          alignment="fill"
          buttons={[
            {
              kind: "Secondary",
              label: "Cancel",
              onClick: () => {
                setShowConfirmation(false);
              }
            },
            {
              label: "Complete transfer",
              onClick: handleCompleteTransfer,
              loading: transferMeta.isLoading
            }
          ]}
        />
      ) : (
        <WSActions
          alignment="fill"
          buttons={[
            {
              kind: "Secondary",
              label: "Cancel",
              onClick: onCancel
            },
            {
              label: "Continue",
              onClick: () => {
                setShowConfirmation(true);
              },
              disabled: !selectedMethod || !amount
            }
          ]}
        />
      )}
    </WSList>
  );
};
